import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../i18n/index.js';
import { cn } from '../lib/cn.js';
import { type IconProps } from '../ui/Icon.js';

/**
 * The application shell: sidebar, top bar, scrolling content.
 *
 * ── SIDEBAR STRUCTURE ─────────────────────────────────────────────────────
 *
 * Both references group navigation under quiet uppercase section labels — the
 * ERP shot uses Business / Management, rekonsile uses MAIN / RECONCILIATION /
 * ANALYTICS / UPLOADS / SUPPORT. v1 was one flat list of eleven items, which is
 * exactly the length at which a flat list stops being scannable.
 *
 * Nested children (Statements, Journal, Ledgers… under Accounting) come from
 * the ERP reference. They expand in place and mark the active leaf with a
 * filled dot.
 *
 * ── RESPONSIVE ────────────────────────────────────────────────────────────
 *
 * Two behaviours, not one:
 *
 *   WIDE   the sidebar is a column, and collapsing it reclaims the width.
 *   NARROW the sidebar is an overlay drawer above the content, because at
 *          768px there is no width to give it — a column that shrinks the
 *          content instead produces a table with three usable columns.
 *
 * The drawer closes on navigation. On a phone, tapping a link and then having
 * to dismiss the menu yourself is the single most common mobile navigation
 * annoyance, and it is one line to avoid.
 */

export interface NavItem {
  id: string;
  label: string;
  icon: (props: IconProps) => ReactNode;
  children?: Array<{ id: string; label: string }>;
}

export interface NavSection {
  label?: string;
  items: NavItem[];
}

export function AppShell({
  sections,
  active,
  onNavigate,
  brand,
  account,
  topbar,
  sidebarOpen,
  collapsed,
  onExpand,
  onSidebarOpenChange,
  overlay,
  children,
}: {
  sections: NavSection[];
  active: string;
  onNavigate: (id: string) => void;
  brand: ReactNode;
  account?: ReactNode;
  topbar?: ReactNode;
  sidebarOpen: boolean;
  /**
   * Desktop only: the sidebar is a 60px icon rail instead of a 256px column.
   * Separate state from `sidebarOpen`, because the drawer and the rail are
   * different affordances that happen to share one button.
   */
  collapsed: boolean;
  /** Called when something needs the full sidebar back — see Sidebar. */
  onExpand: () => void;
  onSidebarOpenChange: (open: boolean) => void;
  /**
   * Floating chrome — the assistant launcher and its panel.
   *
   * A sibling of <main>, not a child, so it does not scroll with page content
   * and does not reset when a page unmounts. Still inside `.cosmos-v2`, because
   * that is where the design tokens are declared and anything rendered outside
   * it would fall back to v1's variables.
   */
  overlay?: ReactNode;
  children: ReactNode;
}) {
  const navigate = (id: string) => {
    onNavigate(id);
    // Close the drawer on navigation. Harmless on wide screens where the
    // sidebar is not a drawer — `lg:` keeps it visible regardless.
    if (window.matchMedia('(max-width: 1023px)').matches) onSidebarOpenChange(false);
  };

  // Escape closes the drawer, which is what every overlay in the app does and
  // what a keyboard user will try first.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSidebarOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen, onSidebarOpenChange]);

  return (
    <div className="cosmos-v2 flex h-dvh w-full overflow-hidden bg-canvas">
      {/* Scrim, drawer only. Sits under the sidebar and over the content. */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label={t('a11y.closeNavigation')}
          onClick={() => onSidebarOpenChange(false)}
          className="fixed inset-0 z-(--p-z-overlay) bg-black/30 backdrop-motion lg:hidden"
        />
      )}

      <Sidebar
        sections={sections}
        active={active}
        onNavigate={navigate}
        brand={brand}
        account={account}
        open={sidebarOpen}
        collapsed={collapsed}
        onExpand={onExpand}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {topbar && (
          <div className="flex h-(--topbar-height) shrink-0 items-center gap-2 border-b border-subtle bg-raised px-(--topbar-padding-x)">
            {topbar}
          </div>
        )}
        {/* Only this scrolls. The sidebar and top bar stay put, which is what
            makes a sticky table header stick to something meaningful. */}
        <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
      {overlay}
    </div>
  );
}

function Sidebar({
  sections,
  active,
  onNavigate,
  brand,
  account,
  open,
  collapsed,
  onExpand,
}: {
  sections: NavSection[];
  active: string;
  onNavigate: (id: string) => void;
  brand: ReactNode;
  account?: ReactNode;
  open: boolean;
  collapsed: boolean;
  onExpand: () => void;
}) {
  // A group opens when something inside it is active, and can also be toggled
  // by hand. Recomputed when `active` changes so a deep link, or a jump from a
  // drill-down, lands with the right group already open.
  const [manual, setManual] = useState<Record<string, boolean>>({});

  const toggle = (id: string) =>
    setManual((current) => ({ ...current, [id]: !isOpen(id) }));

  const open_ = (id: string) => setManual((current) => ({ ...current, [id]: true }));

  const isOpen = (id: string) => {
    const item = sections.flatMap((s) => s.items).find((i) => i.id === id);
    const containsActive = item?.children?.some((c) => c.id === active) ?? false;
    return manual[id] ?? containsActive;
  };

  return (
    <nav
      aria-label={t('a11y.mainNavigation')}
      // `group/nav` + `data-collapsed` so the brand and account blocks — which
      // are passed IN as props and cannot see this component's state — can hide
      // their own labels with `lg:group-data-[collapsed]/nav:hidden`. Prop
      // drilling `collapsed` into every slot would make each caller responsible
      // for remembering it; the attribute means they only opt in where it
      // matters.
      data-collapsed={collapsed || undefined}
      className={cn(
        'group/nav',
        'flex shrink-0 flex-col border-r border-nav-border bg-nav',
        // Drawer below lg, column at lg and up. Collapsing is a DESKTOP-only
        // idea: the drawer already hides completely, so a rail would be a third
        // state that does nothing the closed drawer does not already do.
        'fixed inset-y-0 left-0 z-(--p-z-modal) transition-transform duration-(--m-normal) ease-out',
        'lg:static lg:z-auto lg:translate-x-0 lg:transition-none',
        // Two MUTUALLY EXCLUSIVE variants rather than a base width plus an `lg:`
        // override. The override lost: Tailwind emits `lg:w-(…)` earlier in the
        // sheet than the unprefixed `w-(…)`, they have equal specificity, so the
        // base one won and the rail stayed 256px wide with the correct class on
        // it. Never rely on a variant out-ranking its own base utility.
        'max-lg:w-(--sidebar-width)',
        collapsed ? 'lg:w-(--sidebar-width-collapsed)' : 'lg:w-(--sidebar-width)',
        // Width animates only at lg, where it is the thing that changes. On the
        // drawer the transform is what moves and animating width as well reads
        // as two separate motions.
        'lg:transition-[width] lg:duration-(--m-normal) lg:ease-out',
        open ? 'translate-x-0 shadow-(--shadow-modal)' : '-translate-x-full lg:shadow-none',
      )}
      // Hidden from assistive tech when the drawer is closed, so a screen
      // reader does not read out a menu that is off screen. `lg:` cannot express
      // that, hence the explicit attribute.
      {...(!open ? { 'data-closed': true } : {})}
    >
      <div
        className={cn(
          'flex h-(--topbar-height) shrink-0 items-center px-(--sidebar-padding-x)',
          collapsed && 'lg:justify-center lg:px-0',
        )}
      >
        {brand}
      </div>

      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto px-(--sidebar-padding-x) pb-4',
          collapsed && 'lg:px-2',
        )}
      >
        {sections.map((section, index) => (
          <div key={section.label ?? index} className="mt-(--sidebar-section-gap)">
            {section.label && (
              <>
                <p
                  className={cn(
                    'px-2 pb-(--sidebar-section-label-gap) text-2xs font-semibold uppercase tracking-caps text-nav-section',
                    collapsed && 'lg:hidden',
                  )}
                >
                  {section.label}
                </p>
                {/* A rule stands in for the heading in the rail. Without it the
                    groups run together into one undifferentiated column of
                    icons, and the grouping is half the point of the sidebar. */}
                {collapsed && index > 0 && (
                  <div className="mx-2 mb-1.5 hidden border-t border-nav-border lg:block" />
                )}
              </>
            )}
            <ul className="flex flex-col gap-(--sidebar-item-gap)">
              {section.items.map((item) => {
                const childActive = item.children?.some((c) => c.id === active) ?? false;
                const expanded = item.children ? isOpen(item.id) : undefined;

                return (
                  <li key={item.id}>
                    <NavButton
                      icon={item.icon}
                      label={item.label}
                      // A parent whose child is active is not itself "the page".
                      // Highlighting both makes it look like two things are open.
                      active={active === item.id && !childActive}
                      onClick={() => {
                        if (!item.children) return onNavigate(item.id);
                        // In the rail there is nowhere to show children, so the
                        // group asks for the sidebar back and opens itself. The
                        // alternative — a hover flyout — is a popover component
                        // with its own placement and focus rules, which is more
                        // machinery than one collapsed state earns.
                        if (collapsed) {
                          // Expand and OPEN, not toggle. Someone clicking a
                          // group icon in the rail wants to see what is inside
                          // it; toggling would restore the sidebar and shut the
                          // group in the same click, which reads as the click
                          // having done nothing useful.
                          onExpand();
                          open_(item.id);
                          return;
                        }
                        toggle(item.id);
                      }}
                      expanded={expanded}
                      muted={childActive}
                      collapsed={collapsed}
                    />
                    {/* Children are never shown in the rail. There is no room for
                        a label, and a second column of unlabelled icons would be
                        unreadable — so a group in the rail expands the sidebar
                        instead. See NavButton. */}
                    {/* Rendered whenever the item HAS children, open or not:
                        a transition needs both of its states to exist, so
                        unmounting the children would make the group snap open
                        and animate only on the way shut. */}
                    {item.children && (
                      <div
                        className={cn('disclosure-motion', collapsed && 'lg:hidden')}
                        data-open={expanded ? 'true' : 'false'}
                        // Closed children are unreachable by keyboard and
                        // invisible to a screen reader, not merely 0px tall.
                        aria-hidden={!expanded}
                      >
                      <ul className="flex flex-col gap-(--sidebar-item-gap) pt-(--sidebar-item-gap) pb-1">
                        {item.children.map((child) => (
                          <li key={child.id}>
                            <ChildButton
                              label={child.label}
                              active={active === child.id}
                              onClick={() => onNavigate(child.id)}
                            />
                          </li>
                        ))}
                      </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {account && <div className="shrink-0 border-t border-nav-border p-2">{account}</div>}
    </nav>
  );
}

function NavButton({
  icon: Icon,
  label,
  active,
  onClick,
  expanded,
  muted,
  collapsed,
}: {
  icon: (props: IconProps) => ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  expanded?: boolean;
  muted?: boolean;
  collapsed: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // `aria-current` for a destination, `aria-expanded` for a disclosure.
      // They mean different things and a screen reader says so.
      aria-current={expanded === undefined && active ? 'page' : undefined}
      aria-expanded={expanded}
      // The label is the only thing identifying an icon in the rail, so it has
      // to survive as a tooltip. Native `title` rather than a Tooltip component:
      // it needs no JS, works on focus, and is announced — and a rail is the one
      // place where a delayed tooltip is the right amount of ceremony.
      title={collapsed ? label : undefined}
      className={cn(
        'group flex w-full items-center gap-2.5 rounded-(--sidebar-item-radius)',
        'h-(--sidebar-item-height) px-2 text-md font-medium',
        collapsed && 'lg:justify-center lg:px-0',
        'transition-colors duration-(--m-fast)',
        'focus-visible:outline-none focus-visible:shadow-(--focus-ring)',
        active
          ? 'bg-nav-active text-nav-active-fg'
          : muted
            ? 'text-primary'
            : 'text-nav-fg hover:bg-nav-hover hover:text-primary',
      )}
    >
      <Icon size={16} className="shrink-0" />
      <span
        title={typeof label === 'string' ? label : undefined}
        className={cn('flex-1 truncate text-left', collapsed && 'lg:hidden')}
      >
        {label}
      </span>
      {expanded !== undefined && (
        <ChevronGlyph
          className={cn(
            'text-tertiary transition-transform duration-(--m-fast)',
            expanded && 'rotate-180',
            collapsed && 'lg:hidden',
          )}
        />
      )}
    </button>
  );
}

function ChevronGlyph({ className }: { className?: string }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true"
    >
      <path d="M6 9.5 12 15l6-5.5" />
    </svg>
  );
}

/**
 * A child nav item. The dot marker is the ERP reference's device: hollow when
 * idle, filled when active — so the state is a shape as well as a weight, which
 * survives a colour-blind reader and a greyscale print.
 */
function ChildButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-(--sidebar-item-radius)',
        'h-(--sidebar-item-height) pl-4 pr-2 text-md',
        'transition-colors duration-(--m-fast)',
        'focus-visible:outline-none focus-visible:shadow-(--focus-ring)',
        active
          ? 'text-nav-active-fg font-medium'
          : 'text-nav-fg hover:bg-nav-hover hover:text-primary',
      )}
    >
      <span
        className={cn(
          'size-1.5 shrink-0 rounded-full transition-colors',
          active ? 'bg-nav-active-marker' : 'bg-transparent ring-1 ring-strong',
        )}
        aria-hidden="true"
      />
      <span title={typeof label === 'string' ? label : undefined} className="flex-1 truncate text-left">
        {label}
      </span>
    </button>
  );
}
